import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { LearnAuthProvider } from '../components/learn/LearnAuthProvider';
import { LearnAuthGuard } from '../components/learn/LearnAuthGuard';

/**
 * Pages under /learn that must work WITHOUT an account.
 *
 * An explicit allow-list, not a per-route flag, so that adding a public page is
 * a visible edit to this one line rather than something a new route can acquire
 * by accident — which is the property the blanket gate below was protecting.
 *
 * /learn/feedback is here because it is handed to teachers at demos, on paper
 * or as a QR code. A sign-in wall on a feedback form collects nothing from
 * exactly the people whose answers we most need.
 *
 * The three insight portals are here because they are a MOCKUP — every figure
 * comes from mockData.ts, no API is called and no real learner appears — and
 * their whole job is to be shown to someone who does not have an account yet.
 * They also tab between each other, so gating one gates all three in practice:
 * a teacher clicking "student lens" would hit a login wall mid-demo.
 *
 * Anything added here must be checked for the same two properties: it renders
 * without a session, and it exposes nobody's data.
 */
const PUBLIC_LEARN_PATHS = [
  '/learn/feedback',
  '/learn/teacher',
  '/learn/student',
  '/learn/parent',
];

// Layout for every /learn route. Gating here rather than per-route means a new
// learn page cannot be added without a sign-in requirement by accident — the
// child renders through the Outlet only once a learner session exists.
export const Route = createFileRoute('/learn')({
  component: LearnLayout,
});

function LearnLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isPublic = PUBLIC_LEARN_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  // The provider stays mounted either way: a signed-in visitor on a public page
  // still gets their name and email prefilled, they simply are not required to
  // have either.
  return (
    <LearnAuthProvider>
      {isPublic ? <Outlet /> : (
        <LearnAuthGuard>
          <Outlet />
        </LearnAuthGuard>
      )}
    </LearnAuthProvider>
  );
}
